import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Variable } from '@entity/variable';
import { MessageService, TreeNode } from 'primeng/api';
import { MappedValue, MappedValueBase } from '@entity/catalog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MainDataService } from '@service/main-data.service';
import { CatalogService } from '@service/catalog.service';
import { ButtonModule } from 'primeng/button';
import { TreeModule } from 'primeng/tree';

@Component({
  selector: 'app-mapping-catalog-detail-dialog',
  templateUrl: './mapping-catalog-detail-dialog.component.html',
  styleUrls: ['./mapping-catalog-detail-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TreeModule, ButtonModule, TranslatePipe],
})
export class MappingCatalogDetailDialogComponent implements OnInit {
  private dynamicDialogRef = inject(DynamicDialogRef);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private dynamicDialogConfig = inject(DynamicDialogConfig);
  private catalogService = inject(CatalogService);
  private mainDataService = inject(MainDataService);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);

  value: TreeNode[] = [];
  selection: TreeNode[] = [];
  loading = true;

  private static determineParent(variable: Variable, parentNode?: TreeNode): TreeNode | undefined {
    while (parentNode != null && !variable.tacsId?.startsWith(parentNode?.data.tacsId)) {
      parentNode = parentNode?.parent;
    }
    return parentNode;
  }

  private createNode(
    value: MappedValue,
    isSelectedOrMapped: boolean | MappedValue | undefined,
    translateService: TranslateService
  ): TreeNode {
    let label = value.tacsId + ' ' + value.name;
    if (value.validTo) {
      label += ` (${value.validTo.toLocaleString('de-CH', { dateStyle: 'medium' })})`;
    }
    const validNow = this.isValidNow(value);
    return {
      children: [],
      expanded: !!value.mappedId,
      key: value.id + '|' + value.type,
      data: {
        ...value,
      },
      label: validNow ? label : `(${translateService.instant('GLOBAL.INACTIVE')}) ${label}`,
      selectable: validNow || !!isSelectedOrMapped,
    };
  }

  private isValidNow(mappedValue: MappedValue): boolean {
    return mappedValue.validFrom.valueOf() < Date.now() && mappedValue.validTo
      ? mappedValue.validTo.valueOf() > Date.now()
      : true;
  }

  ngOnInit(): void {
    const { mandantId, catalogId, detailId, selectedColumns, tacsVariables } = this.dynamicDialogConfig.data;
    if (detailId) {
      const action =
        mandantId == null
          ? this.mainDataService.getCatalogDetailMappings(catalogId, detailId)
          : this.catalogService.getCatalogDetailMappings(mandantId, catalogId, detailId);

      action.subscribe((result) => {
        const mappedValueMap = result.reduce((m, v) => m.set(v.id, v), new Map<number, MappedValueBase>());
        tacsVariables.forEach((mappedValue: MappedValue) => {
          const mappedValueBase = mappedValueMap.get(mappedValue.id);
          if (mappedValueBase != null) {
            mappedValue.mappedId = mappedValueBase?.mappedId;
            mappedValue.type = mappedValueBase?.type;
          }
        });
        this.updateTreeValue(tacsVariables, selectedColumns ?? []);
        this.loading = false;
      });
    } else {
      this.updateTreeValue(tacsVariables, selectedColumns ?? []);
      this.loading = false;
    }
  }

  save(): void {
    const { mandantId, catalogId, detailId, mappingType } = this.dynamicDialogConfig.data;

    let newMapping = this.selection.map((s) => s.data);
    if (mappingType === 'Measure') {
      newMapping = newMapping.filter((m) => m.tacsId.split('.').length === 6);
    }

    if (!detailId) {
      this.dynamicDialogRef.close(newMapping);
      return;
    }

    const action =
      mappingType === 'Measure'
        ? mandantId == null
          ? this.mainDataService.updateMeasureMappings(catalogId, detailId, newMapping)
          : this.catalogService.updateMeasureMappings(mandantId, catalogId, detailId, newMapping)
        : mandantId == null
          ? this.mainDataService.updateVariableMappings(catalogId, detailId, newMapping)
          : this.catalogService.updateVariableMappings(mandantId, catalogId, detailId, newMapping);

    action.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('GLOBAL.SUCCESSFULLY_SAVED'),
        });
        this.dynamicDialogRef.close(newMapping);
      },
      error: () =>
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('GLOBAL.NOT_SUCCESSFULLY_SAVED'),
        }),
    });
  }

  cancel(): void {
    this.dynamicDialogRef.close();
  }

  getAllActiveMappedValues(
    $event: TreeNode<MappedValue> | TreeNode<MappedValue>[] | null | undefined
  ): TreeNode<MappedValue>[] {
    if (Array.isArray($event)) {
      return $event.filter((e) => this.isValidNow(e.data!));
    }
    return $event ? [$event] : [];
  }

  private updateTreeValue(mappedValues: MappedValue[], selectedColumns: MappedValue[]): void {
    mappedValues.sort((a, b) => ((a.tacsId ?? '') < (b.tacsId ?? '') ? -1 : 1));
    this.value = [];
    let parentNode: TreeNode | undefined;
    mappedValues.forEach((mappedValue) => {
      const isSelectedOrMapped = !!mappedValue.mappedId || selectedColumns.find((c) => c.id === mappedValue.id);
      const newNode = this.createNode(mappedValue, isSelectedOrMapped, this.translateService);
      if (isSelectedOrMapped) {
        this.selection.push(newNode);
        newNode.expanded = true;
      }
      const newParentNode = MappingCatalogDetailDialogComponent.determineParent(mappedValue, parentNode);
      if (newParentNode) {
        if (newParentNode.expanded || newNode.expanded) {
          newParentNode.expanded = true;
          if (newParentNode.parent) {
            newParentNode.parent.expanded = true;
          }
        }
        newParentNode.children?.push(newNode);
        newNode.parent = newParentNode;
        if (parentNode !== newParentNode) {
          parentNode = newParentNode;
        }
      } else {
        this.value.push(newNode);
      }
      parentNode = newNode;
    });
    this.changeDetectorRef.markForCheck();
  }
}
